'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdBed, MdSave, MdArrowBack } from 'react-icons/md'
import {
    getBed,
    createBed,
    updateBed,
    getUnits,
    CreateBedData,
    BedType,
    HospitalUnit,
    BED_TYPE_LABELS,
} from '@/lib/api/hospital'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function BedFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = Boolean(id)

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [units, setUnits] = useState<HospitalUnit[]>([])
    const { hospitals, loading: loadingHospitals, selectedHospitalId, setSelected } = useHospitals()
    const [formData, setFormData] = useState<CreateBedData>({
        unitId: 0,
        number: '',
        type: 'STANDARD',
        hasOxygen: false,
        hasVacuum: false,
        hasAirConditioning: false,
        hasBathroom: false,
        isPrivate: false,
        dailyRate: undefined,
        notes: '',
    })
    const [active, setActive] = useState(true)

    useEffect(() => {
        if (isEditing && id) {
            loadBed(parseInt(id))
        }
    }, [id, isEditing])

    useEffect(() => {
        if (selectedHospitalId) {
            loadUnits(selectedHospitalId)
        }
    }, [selectedHospitalId])

    const loadUnits = async (hospitalId: number) => {
        try {
            const response = await getUnits(undefined, hospitalId)
            if (response.success) {
                setUnits(response.result.filter(u => u.active))
            }
        } catch (error) {
            console.error('Error loading units:', error)
        }
    }

    const loadBed = async (bedId: number) => {
        setLoading(true)
        try {
            const response = await getBed(bedId)
            if (response.success) {
                const bed = response.result
                setFormData({
                    unitId: bed.unitId,
                    number: bed.number,
                    type: bed.type,
                    hasOxygen: bed.hasOxygen,
                    hasVacuum: bed.hasVacuum,
                    hasAirConditioning: bed.hasAirConditioning,
                    hasBathroom: bed.hasBathroom,
                    isPrivate: bed.isPrivate,
                    dailyRate: bed.dailyRate,
                    notes: bed.notes || '',
                })
                setActive(bed.active)
            } else {
                toast.error(response.error || 'Erro ao carregar leito')
                navigate('/hospital/beds')
            }
        } catch (error) {
            toast.error('Erro ao carregar leito')
            navigate('/hospital/beds')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.unitId) {
            toast.error('Selecione uma unidade')
            return
        }

        if (!formData.number.trim()) {
            toast.error('Número do leito é obrigatório')
            return
        }

        setSaving(true)
        try {
            const data = {
                ...formData,
                notes: formData.notes || undefined,
            }

            let response
            if (isEditing && id) {
                response = await updateBed(parseInt(id), { ...data, active })
            } else {
                response = await createBed(data)
            }

            if (response.success) {
                toast.success(
                    isEditing ? 'Leito atualizado com sucesso' : 'Leito criado com sucesso'
                )
                navigate('/hospital/beds')
            } else {
                toast.error(response.error || 'Erro ao salvar leito')
            }
        } catch (error) {
            toast.error('Erro ao salvar leito')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando leito...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/hospital/beds')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdBed className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Leito' : 'Novo Leito'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados do leito'
                                : 'Cadastre um novo leito'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-2">Hospital</h3>
                    <p className="text-sm text-base-content/60 mb-3">
                        Selecione o hospital para listar suas unidades
                    </p>
                    <HospitalSelector
                        hospitals={hospitals}
                        selectedId={selectedHospitalId}
                        onChange={id => {
                            setSelected(id)
                            setFormData({ ...formData, unitId: 0 })
                        }}
                        loading={loadingHospitals}
                    />
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Informações Básicas</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Unidade *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.unitId}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        unitId: parseInt(e.target.value),
                                    })
                                }
                                required
                            >
                                <option value={0}>Selecione uma unidade</option>
                                {units.map(unit => (
                                    <option key={unit.id} value={unit.id}>
                                        {unit.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Número *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: 101"
                                value={formData.number}
                                onChange={e =>
                                    setFormData({ ...formData, number: e.target.value })
                                }
                                required
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Tipo *</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.type}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        type: e.target.value as BedType,
                                    })
                                }
                                required
                            >
                                {Object.entries(BED_TYPE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">Diária (R$)</span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered"
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                value={formData.dailyRate || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        dailyRate: e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Recursos</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.hasOxygen}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            hasOxygen: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">Oxigênio</span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.hasVacuum}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            hasVacuum: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">Vácuo</span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.hasAirConditioning}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            hasAirConditioning: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">Ar Condicionado</span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.hasBathroom}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            hasBathroom: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">Banheiro Privativo</span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-4">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.isPrivate}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            isPrivate: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">Quarto Privativo</span>
                            </label>
                        </div>

                        {isEditing && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-4">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={active}
                                        onChange={e => setActive(e.target.checked)}
                                    />
                                    <span className="label-text">Ativo</span>
                                </label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="font-bold text-lg mb-4">Observações</h3>
                    <div className="form-control">
                        <textarea
                            className="textarea textarea-bordered h-24"
                            placeholder="Observações sobre o leito..."
                            value={formData.notes}
                            onChange={e =>
                                setFormData({ ...formData, notes: e.target.value })
                            }
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/hospital/beds')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
